import { makeEsArticle, esSaintRelated } from "./learnFactoryEs";

/** Spanish: /es/learn/when-antidepressants-dont-work */
export const esPageWhenAntidepressantsDontWork = makeEsArticle({
  slug: "when-antidepressants-dont-work",
  title: "Cuando los antidepresivos no funcionan | Neuro Wellness",
  description:
    "Qué hacer cuando los antidepresivos no dan suficiente alivio: optimización de medicación, psicoterapia, TMS, SAINT® acelerada, SPRAVATO y ketamina. Opciones y próximos pasos.",
  h1: "Cuando los antidepresivos no funcionan",
  breadcrumbLabel: "Cuando los antidepresivos no funcionan",
  shortAnswer:
    "Si los antidepresivos no han producido una mejora suficiente, existen opciones: optimización o aumento de la medicación, psicoterapia, TMS convencional, TMS acelerada (SAINT®), SPRAVATO (esketamina) y ketamina. Una evaluación psiquiátrica revisa si los intentos previos fueron adecuados, descarta un diagnóstico erróneo (como el trastorno bipolar) y recomienda el siguiente paso más apropiado. Los resultados individuales pueden variar.",
  keyTakeaways: [
    "La falta de respuesta a los antidepresivos no significa que no haya opciones.",
    "Un intento incompleto (suspendido pronto o sin aumentar la dosis) no es lo mismo que un intento fallido.",
    "La TMS está autorizada por la FDA para adultos con depresión mayor que no mejoraron con antidepresivos.",
    "SAINT® es una TMS acelerada: 10 sesiones al día durante 5 días con guía por fMRI.",
    "Una consulta psiquiátrica determina la elegibilidad; los resultados individuales pueden variar.",
  ],
  sections: [
    {
      heading: "Qué significa que los antidepresivos no funcionen",
      paragraphs: [
        "Que un antidepresivo «no funcione» puede significar varias cosas: alivio parcial, ningún alivio, o alivio inicial seguido de recaída. Antes de concluir que la depresión es resistente al tratamiento, un psiquiatra revisa si cada medicamento se probó a una dosis terapéutica durante el tiempo suficiente (por lo general unas seis a ocho semanas). Un medicamento suspendido pronto por efectos secundarios, o que nunca se aumentó desde la dosis inicial, suele considerarse un intento incompleto y no un intento fallido.",
        "Esta distinción es importante. La depresión resistente al tratamiento, por lo general, requiere al menos dos intentos adecuados. Un psiquiatra puede revisar su historial y determinar si los intentos previos cuentan como adecuados.",
      ],
    },
    {
      heading: "Opciones cuando los antidepresivos no son suficientes",
      paragraphs: ["Entre las opciones se encuentran:"],
      list: [
        "Optimización de la medicación: ajustar la dosis o cambiar a otro antidepresivo dentro o entre clases.",
        "Aumento: añadir un segundo medicamento (p. ej., un antipsicótico atípico, litio u hormona tiroidea) al antidepresivo actual.",
        "Psicoterapia: terapia cognitivo-conductual u otra terapia basada en evidencia, sola o con medicación.",
        "TMS convencional: autorizada por la FDA para adultos con depresión mayor que no mejoraron con antidepresivos.",
        "TMS acelerada (SAINT®): 10 sesiones al día durante 5 días con guía por fMRI, autorizada por la FDA.",
        "SPRAVATO® (esketamina): aerosol nasal aprobado por la FDA para la depresión resistente al tratamiento.",
        "Ketamina: infusiones IV de ketamina, una opción no aprobada por la FDA para la depresión.",
      ],
    },
    {
      heading: "¿Puede la TMS ayudar si los antidepresivos no funcionaron?",
      paragraphs: [
        "Sí. La TMS convencional está autorizada por la FDA específicamente para adultos con trastorno depresivo mayor que no han mejorado con medicación antidepresiva. SAINT® es una versión acelerada. Ambas requieren una evaluación psiquiátrica para confirmar la elegibilidad. Los resultados individuales pueden variar.",
      ],
    },
    {
      heading: "No suspenda su antidepresivo por su cuenta",
      paragraphs: [
        "Suspender de forma abrupta puede causar síntomas de discontinuación y aumenta el riesgo de recaída. Si desea reducir o suspender la medicación, pida a su médico que planee una reducción gradual. Muchos tratamientos —incluida la TMS y la psicoterapia— se administran junto con la medicación, no en su lugar.",
      ],
    },
    {
      heading: "Próximos pasos",
      paragraphs: [
        "Si los antidepresivos no le han dado suficiente alivio, el siguiente paso es una evaluación con un psiquiatra. En Neuro Wellness TMS Centers of America ofrecemos SAINT®, TMS convencional, SPRAVATO®, ketamina y psicoterapia, y verificamos sus beneficios del seguro sin costo.",
        "[[Solicite una consulta gratuita|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs: [
    {
      question: "¿Qué puedo probar cuando los antidepresivos no funcionan?",
      answer:
        "Si los antidepresivos no han producido una mejora suficiente, las opciones incluyen optimización o aumento de la medicación, psicoterapia, TMS convencional, TMS acelerada (SAINT®), SPRAVATO (esketamina) y ketamina. Una evaluación psiquiátrica revisa si los intentos previos fueron adecuados, descarta un diagnóstico erróneo (como el trastorno bipolar) y recomienda el siguiente paso más apropiado.",
    },
    {
      question:
        "¿«No funcionar» significa que mi depresión es resistente al tratamiento?",
      answer:
        "No necesariamente. Un medicamento suspendido pronto por efectos secundarios, o que nunca se aumentó desde la dosis inicial, suele considerarse un intento incompleto y no uno fallido. La depresión resistente al tratamiento, por lo general, requiere al menos dos intentos adecuados —dosis terapéutica durante unas seis a ocho semanas. Un psiquiatra puede revisar su historial.",
    },
    {
      question: "¿Debo suspender mi antidepresivo si no está funcionando?",
      answer:
        "No. Suspender de forma abrupta puede causar síntomas de discontinuación y aumenta el riesgo de recaída. Si desea reducir o suspender la medicación, pida a su médico que planee una reducción gradual. Muchos tratamientos —incluida la TMS y la psicoterapia— se administran junto con la medicación, no en su lugar.",
    },
    {
      question: "¿Puede la TMS ayudar si los antidepresivos no funcionaron?",
      answer:
        "Sí. La TMS convencional está autorizada por la FDA específicamente para adultos con trastorno depresivo mayor que no han mejorado con medicación antidepresiva. SAINT® es una versión acelerada. Ambas requieren una evaluación psiquiátrica para confirmar la elegibilidad.",
    },
    {
      question:
        "¿Cuál es la diferencia entre respuesta insuficiente y depresión resistente al tratamiento?",
      answer:
        "Respuesta insuficiente significa que un tratamiento no ha producido suficiente mejora. La depresión resistente al tratamiento es un término clínico para la depresión que no ha respondido a al menos dos intentos adecuados de antidepresivos. La respuesta insuficiente es motivo para reevaluar; la TRD es un patrón que guía los próximos pasos.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  relatedLinks: esSaintRelated,
});
